export type Breadcrumb = {
  name?: string;
  path: string;
};

type Route = {
  path: string;
  name?: string;
  parent?: string;
};

const defineRoutes = <T extends { [K in keyof T]: Route & { parent?: keyof T } }>(routes: T) => routes;

const ROUTES = defineRoutes({
  home: { path: '/', name: 'Home' },
  services: { path: '/services', name: 'Services', parent: 'home' },
  service: { path: '/:slug', parent: 'services' },
  cases: { path: '/cases', name: 'Cases', parent: 'home' },
  case: { path: '/cases/:slug', parent: 'cases' },
  blog: { path: '/blog', name: 'Blog', parent: 'home' },
  article: { path: '/blog/:slug', parent: 'blog' },
  author: { path: '/blog/author/:slug', parent: 'blog' },
  category: { path: '/blog/category/:slug', parent: 'blog' },
  company: { path: '/company', name: 'Company', parent: 'home' },
  contacts: { path: '/contacts', name: 'Contacts', parent: 'home' },
  education: { path: '/career/education', name: 'Education', parent: 'home' },
  vacancies: { path: '/career/vacancies', name: 'Vacancies', parent: 'home' },
  vacancy: { path: '/career/vacancies/:slug', parent: 'vacancies' },
  doc: { path: '/docs/:slug' },
} as const);

export type RouteName = keyof typeof ROUTES;

type ParamNames<Path extends string> = Path extends `${string}:${infer Name}/${infer Rest}`
  ? Name | ParamNames<Rest>
  : Path extends `${string}:${infer Name}` ? Name : never;

type RouteParams<R extends RouteName> = ParamNames<typeof ROUTES[R]['path']>;

export type StaticRouteName = { [R in RouteName]: [RouteParams<R>] extends [never] ? R : never }[RouteName];

type PathArgs<R extends RouteName> = [RouteParams<R>] extends [never]
  ? []
  : [params: Record<RouteParams<R>, string>];

type TrailArgs<R extends RouteName> = [RouteParams<R>] extends [never]
  ? []
  : [params: Record<RouteParams<R>, string>, name: string, via?: Breadcrumb[]];

const routeOf = (route: RouteName): Route => ROUTES[route];

const fillPath = (path: string, params: Record<string, string> = {}) => path.replace(/:(\w+)/g, (_, name: string) => params[name]);

const ancestorsOf = (route: RouteName): RouteName[] => {
  const parent = routeOf(route).parent as RouteName | undefined;

  return parent ? [...ancestorsOf(parent), parent] : [];
};

export const pathTo = <R extends RouteName>(route: R, ...args: PathArgs<R>) => (
  fillPath(routeOf(route).path, (args as unknown[])[0] as Record<string, string> | undefined)
);

export const breadcrumbTrail = <R extends RouteName>(route: R, ...args: TrailArgs<R>): Breadcrumb[] => {
  const [params, name, via = []] = args as unknown as [Record<string, string>?, string?, Breadcrumb[]?];

  return [
    ...ancestorsOf(route).map((ancestor) => ({ name: routeOf(ancestor).name, path: routeOf(ancestor).path })),
    ...via,
    { name: name ?? routeOf(route).name, path: fillPath(routeOf(route).path, params) },
  ];
};

type TitledPage = {
  title: string;
  slug: string;
};

export const articleBreadcrumbs = ({ title, slug, category }: TitledPage & { category?: TitledPage | null }) => breadcrumbTrail(
  'article',
  { slug },
  title,
  category?.slug ? [{ name: category.title, path: pathTo('category', { slug: category.slug }) }] : [],
);
